// ─── useDraft — per-chat composer draft that survives composer remounts ──────────
//
// AppShell mounts every view with key={chatId}, so switching chats — OR switching
// view mode (WhimerClean² ↔ WhimerTerm² ↔ clean ↔ terminal) on the same chat, which
// swaps the component type — remounts the composer and would wipe a locally-held
// draft. Keying the draft on chatId in a store that lives OUTSIDE React (module Map +
// localStorage) means each chat keeps its own unsent text across both kinds of switch,
// and across a page reload too. Shared by MessageComposer and WhimerComposer so the
// behavior is identical wherever the user types.

import { useState, useCallback, useEffect } from 'react'

const lsKey = (chatId: string) => `whimer.draft.${chatId}`

// In-memory mirror — the source of truth during a session; localStorage is the backing
// store so drafts also survive a full reload. Map read avoids a JSON/localStorage hit
// on every keystroke.
const mem = new Map<string, string>()

function load(chatId: string): string {
  if (mem.has(chatId)) return mem.get(chatId)!
  let v = ''
  try { v = localStorage.getItem(lsKey(chatId)) ?? '' } catch { /* private mode / no storage */ }
  mem.set(chatId, v)
  return v
}

function save(chatId: string, text: string) {
  mem.set(chatId, text)
  try {
    if (text) localStorage.setItem(lsKey(chatId), text)
    else localStorage.removeItem(lsKey(chatId)) // empty draft → drop the key, no stale buildup
  } catch { /* quota / unavailable — in-memory mirror still holds it for the session */ }
}

type Updater = string | ((prev: string) => string)

/**
 * Per-chat draft text. Returns [text, setText, clear] mirroring useState's setter
 * (string or updater fn), but persisted per chatId so it outlives composer remounts.
 */
export function useDraft(chatId: string): [string, (next: Updater) => void, () => void] {
  const [text, setText] = useState(() => load(chatId))

  // Defensive: if chatId changes without a remount, re-read that chat's draft.
  useEffect(() => { setText(load(chatId)) }, [chatId])

  const set = useCallback((next: Updater) => {
    setText(prev => {
      const value = typeof next === 'function' ? next(prev) : next
      save(chatId, value)
      return value
    })
  }, [chatId])

  const clear = useCallback(() => set(''), [set])

  return [text, set, clear]
}
